/** Presentation of the planner's queue; filtering never changes its order. */
import { hourRangeLabel, type DropGenListRankingPreview, type DropGenRankedList } from '@trafficpulse/shared';

import { excludeHit } from '../../../rules/rule-model';
import type { QueueRow } from '../../queue/QueueList';

export type ResultKind = 'ranked' | 'no-traffic' | 'excluded' | 'pending';
export type ResultFilter = 'all' | Exclude<ResultKind, 'pending'>;
export interface ListResult extends QueueRow {
  position: number;
  kind: ResultKind;
  reason: string;
  score: number | null;
  visits: number | null;
  tags: string[];
  source?: DropGenRankedList;
}

const reasonOf = (list: DropGenRankedList | undefined, range: string): Pick<ListResult, 'kind' | 'reason'> => {
  if (!list) return { kind: 'pending', reason: 'Waiting for a ranking result.' };
  if (!list.unranked) return { kind: 'ranked', reason: 'Ordered by formula score, highest first.' };
  const hit = excludeHit(list.ruleHits);
  if (hit) return { kind: 'excluded', reason: `${hit.name}: ${hit.reason}` };
  if (list.uniqueVisits === 0) return { kind: 'no-traffic', reason: `No traffic in ${range}.` };
  return { kind: 'excluded', reason: 'Not ranked by this formula.' };
};

export function listResults(ordered: QueueRow[], preview?: DropGenListRankingPreview): ListResult[] {
  const byId = new Map(preview?.lists.map((list) => [list.wiserId, list]));
  const range = preview ? hourRangeLabel(preview.primaryRangeKey) : '';
  return ordered.map((row, index) => {
    const list = byId.get(row.wiserId);
    return { ...row, position: index + 1, ...reasonOf(list, range), score: list?.score ?? null, visits: list?.uniqueVisits ?? null, tags: list?.tags ?? [], source: list };
  });
}

export function filterResults(rows: ListResult[], filter: ResultFilter, search: string): ListResult[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => (filter === 'all' || row.kind === filter)
    && (!needle || [row.name ?? '', String(row.wiserId), ...row.tags].some((value) => value.toLowerCase().includes(needle))));
}

export const RESULT_LABEL: Record<ResultKind, string> = {
  ranked: 'Ranked', 'no-traffic': 'No traffic', excluded: 'Rule excluded', pending: 'Pending',
};
